import React from "react";
import { getEarthRadius } from "../utils/units";

/**
 * Calculate distance between two coordinates using Haversine formula
 * @param {Object} userCoords - User coordinates {lat, lng}
 * @param {Object} noteCoords - Note coordinates {lat, lng}
 * @param {'miles'|'km'} unit - Unit for distance calculation (default: 'miles')
 * @returns {number|null} Distance rounded to 1 decimal place, or null if invalid
 */
export function useDistance(userCoords, noteCoords, unit = 'miles') {
  return React.useMemo(() => {
    if (!userCoords || !noteCoords) return null;
    const { lat: lat1, lng: lon1 } = userCoords;
    const { lat: lat2, lng: lon2 } = noteCoords;
    if ([lat1, lon1, lat2, lon2].some((v) => typeof v !== "number"))
      return null;

    const toRad = (d) => (d * Math.PI) / 180;
    const R = getEarthRadius(unit); // Get Earth radius in specified unit
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    return Math.round(distance * 10) / 10;
  }, [userCoords, noteCoords, unit]);
}
